import { Box, Button, Dialog, DialogActions, DialogContent, Grid, Icon, IconButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { CustomDialogTitle } from "../../../Common/Custom/CustomDialogTitle";
import { BEEScorecardService } from "../../../../Component/Api/BEEScorecard/BEEScorecard";
import { toast } from "react-toastify";
import { SubAdminRole, UserAdminRole, BEELockScorecards } from '../../../../Component/Common/Constants/UserRoles';
import { userRolesState } from '../../../../State/user.Atom';
import { useRecoilValue } from "recoil";

interface IProps {
    id: string;
    open: boolean;
    close: () => void;
}

export const ScorecardLockModal = (props: IProps) => {
    const lockItems = [
        { id: 1, label: "All Scorecard views", lockType: "AllLock" },
        { id: 2, label: "Actual & Data Pages", lockType: "ActualLock" },
        { id: 3, label: "Own Target", lockType: "OwnTargetLock" },
        { id: 4, label: "Scenario", lockType: "ScenarioLock" },
    ];

    const [locks, setLocks] = useState([false, false, false, false]);
    const userRoles = useRecoilValue(userRolesState);
    const hasLockPermission = userRoles.includes(UserAdminRole) || 
                              (userRoles.includes(SubAdminRole) && userRoles.includes(BEELockScorecards));

    useEffect(() => {
        if (props.open) {
            fetchLockStates();
        }
    }, [props.id, props.open]);

    const fetchLockStates = async () => {
        try {
            const response = await BEEScorecardService.getScorecardMetaViewModel(props.id);

            const newLocks = [
                response.data.IsAllLocked ?? false,
                response.data.IsActualLocked ?? false,
                response.data.IsOwnTargetLocked ?? false,
                response.data.IsScenarioLocked ?? false,
            ];

            setLocks(newLocks);
        } catch (error) {
            console.error("Error fetching lock states:", error);
        }
    };

    const handleLockClick = async (id: number) => {

        if (!hasLockPermission) return;
        let newLocks = [...locks];

        if (id === 0) {
            const newState = !locks[0];
            newLocks = newLocks.map(() => newState);
            setLocks(newLocks);

            try {
                await Promise.all(
                    lockItems.map(item =>
                        BEEScorecardService.updateBEEScorecardLock({
                            scorecardId: props.id,
                            lockType: item.lockType,
                            isLocked: newState,
                        })
                    )
                );
            } catch (error) {
                toast.error("Failed to update lock states");
                setLocks(locks);
            }
        } else {
            newLocks[id] = !newLocks[id];
            setLocks(newLocks);

            try {
                await BEEScorecardService.updateBEEScorecardLock({
                    scorecardId: props.id,
                    lockType: lockItems[id].lockType,
                    isLocked: newLocks[id]
                });
            } catch (error) {
                newLocks[id] = !newLocks[id];
                setLocks(newLocks);
                toast.error("Failed to update lock state");
            }
        }
    };

    return (
        <Dialog disableEnforceFocus open={props.open} onClose={props.close} maxWidth="sm">
            <CustomDialogTitle id="locks-dialog-title" onClose={props.close}>
                Scorecard Locks
            </CustomDialogTitle>

            <DialogContent sx={{ height: 230, width: 500 }}>
                <Grid container paddingLeft={2}>
                    <Grid item xs={12}>
                        {!hasLockPermission && (
                            <Typography color="error" fontSize={14}>
                                Locks are only available to Admin users and Sub-Admin users with the Lock/Unlock Scorecards role.
                            </Typography>
                        )}
                        {lockItems.map((item, id) => {
                            const isDisabled = !hasLockPermission || (locks[0] && id !== 0);
                            return (
                                <Box key={item.id} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography fontSize={16}>{item.label}</Typography>
                                    <IconButton 
                                        onClick={() => handleLockClick(id)}
                                        disabled={isDisabled}>
                                        <Icon sx={{ color: isDisabled ? 'grey.500' : 'primary.main' }}>
                                            {locks[id] ? "lock" : "lock_open"}
                                        </Icon>
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={props.close} color="primary" variant="contained">
                    Done
                </Button>
            </DialogActions>
            
        </Dialog>
    );
};